import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PlatformStats {
  totalAssessments: number;
  totalSessions: number;
  /** null until the server confirms a real value */
  patientsScreened: number | null;
  /** null until the server confirms a real value */
  accuracyRate: number | null;
  isOperational: boolean;
}

const defaultStats: PlatformStats = {
  totalAssessments: 0,
  totalSessions: 0,
  // Do NOT use placeholder numbers here — they would be displayed as real data
  // on the public landing page. Keep null until the edge function responds.
  patientsScreened: null,
  accuracyRate: null,
  isOperational: true,
};

export const usePlatformStats = () => {
  const [stats, setStats] = useState<PlatformStats>(defaultStats);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('platform-stats');

        if (error) {
          if (import.meta.env.DEV) console.debug('[usePlatformStats] Failed to fetch stats:', error);
          return;
        }

        setStats({
          totalAssessments: data.totalAssessments ?? 0,
          totalSessions: data.totalSessions ?? 0,
          // Only accept real numbers from the server; null means "not available"
          patientsScreened: typeof data.patientsScreened === 'number' ? data.patientsScreened : null,
          accuracyRate: typeof data.accuracyRate === 'number' ? data.accuracyRate : null,
          isOperational: data.isOperational ?? true,
        });
      } catch (error) {
        if (import.meta.env.DEV) console.debug('[usePlatformStats] Stats fetch error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, isLoading };
};
